<!--Translated-->
<template>
  <article class="rightzone">
    <div class="rightzone_top">
      <Breadcrumps :data="[{ title: $t('UserDefence:Title') }]" />

      <div class="rightzone_top-buttonswrap">
        <Button
          :text="$t('UserDefence:ButtonAdd')"
          size="standart"
          color="accent"
          icon="plus"
          :is-loading="false"
          :loading-left="false"
          @click="$router.push({ name: 'UserDefenceProgramCreate' })"
        />
      </div>
    </div>

    <div class="rightzone_block">
      <div class="filter">
        <div class="filter-inputszone">
          <div class="filterwrap filterwrap--clients">
            <div class="filterwrap_line">
              <Input
                type="text"
                :placeholder="$t('UserDefence:Filter:Name.Placepolder')"
                :label="$t('UserDefence:Filter:Name.Label')"
                icon="search"
                size="long"
                v-model="filters.name"
              />
              <DropdownSelectValue
                :placeholder="$t('UserDefence:Filter:Culture.Placepolder')"
                :label="$t('UserDefence:Filter:Culture.Label')"
                :options="culturesFiltered"
                v-model="filters.culture"
              />
              <DropdownSelectValue
                :placeholder="$t('UserDefence:Filter:Region.Placepolder')"
                :label="$t('UserDefence:Filter:Region.Label')"
                :options="regionsFiltered"
                v-model="filters.geo"
              />
            </div>
          </div>
        </div>

        <div class="filter-clearbtnzone">
          <Button
            class="filter_buttonclear"
            text=""
            size="s-standart"
            icon="backspace"
            :is-loading="false"
            :loading-left="true"
            :color="filtersApplied ? 'accentlines' : 'graylines'"
            @click="reset"
          />
        </div>
      </div>
    </div>

    <div class="rightzone_table">
      <Table
        :headers="headers"
        :dataArr="rows"
        @editClick="editProgram"
        @printClick="printProgram"
        :tableLoading="isLoading"
        :loadingCountItems="10"
      >
        <template v-slot:warning-icons>
          <div v-svg="{ name: 'warningdiese' }"></div>
          <div v-svg="{ name: 'user' }"></div>
        </template>
      </Table>
    </div>

    <PrintModal
      v-if="selectedPrintId"
      :clientArea="true"
      @close="selectedPrintId = null"
      :selectedPrintId="selectedPrintId"
    />
  </article>
</template>

<script>
import isEqual from 'lodash.isequal';
import uniqWith from 'lodash.uniqwith';
import { mapGetters, mapState } from 'vuex';

import routeNames from '@/router/routeNames';

import Breadcrumps from '@/components/views/Breadcrumps';
import DropdownSelectValue from '@/components/views/wrappers/DropdownSelectValue';
import Button from '@/components/views/Button';
import Input from '@/components/views/Input';
import Table from '@/components/tables/Table';
import PrintModal from '@/components/modals/ModalPrint';

import { staticData } from '@/store/modules/plantsCare';
import { mapInputEntityWithMerge } from '@/components/adapters/mapInput';

const defaultFilters = {
  name: '',
  culture: null,
  geo: null,
};

export default {
  name: 'DefensePrograms',
  components: {
    Input,
    Button,
    Breadcrumps,
    Table,
    DropdownSelectValue,
    PrintModal,
  },
  data() {
    return {
      filters: { ...defaultFilters },
      selectedPrintId: null,
    };
  },
  computed: {
    ...mapGetters(['geoMapped', 'geoPlainDataAsMap']),
    ...mapState({
      isLoading: (state) => state.userProgramsList.isLoading,
      programs(state) {
        return state.userProgramsList.programs.map((p) => ({
          ...p,
          culture: this.cultures.find(({ id }) => p.culture_id === id),
          methodName:
            staticData.calculationMethodsShort[p.calculation_method_id],
          region:
            this.geoPlainDataAsMap
              .get(p.geo_id)
              ?.order?.map(({ id }) => this.geoPlainDataAsMap.get(id))
              .find(({ type }) => type === 1) ?? null,
        }));
      },
      cultures: (state) =>
        state.userProgramsList.cultures.map(mapInputEntityWithMerge),
    }),
    headers() {
      return [
        {
          text: this.$t('UserDefence:Table:Header:0'),
          type: 'text',
          width: '40%',
        },
        {
          text: this.$t('UserDefence:Table:Header:1'),
          type: 'text',
          width: '15%',
        },
        {
          text: this.$t('UserDefence:Table:Header:2'),
          type: 'text',
          width: '10%',
        },
        {
          text: this.$t('UserDefence:Table:Header:3'),
          type: 'text',
          width: '10%',
        },
        {
          text: this.$t('UserDefence:Table:Header:4'),
          type: 'text',
          width: '15%',
        },
        {
          text: this.$t('UserDefence:Table:Header:5'),
          type: 'text',
          width: '10%',
        },
        {
          class: 'col-edit unicol-colordark',
          type: 'print',
          icon: 'print',
        },
        {
          class: 'col-edit',
          type: 'edit',
          icon: 'edit',
        },
      ];
    },
    culturesFiltered() {
      return this.cultures.filter(({ id }) =>
        this.programs.some(({ culture }) => culture.id === id)
      );
    },
    regionsFiltered() {
      return uniqWith(
        this.programs.map((p) => p.region).filter(Boolean),
        (a, b) => a.id === b.id
      ).map(mapInputEntityWithMerge);
    },
    filtersApplied() {
      return !isEqual(this.filters, defaultFilters);
    },
    rows() {
      const f = this.filters;

      const mapToRow = (input) =>
        [
          input.name,
          input.culture.name,
          input.culture_age,
          input.methodName,
          input.region?.name,
          input.place_area,
          input.id,
          input.id,
        ].map((text) => ({ text }));

      const filter = (row) =>
        row.name.includes(f.name) &&
        (f.geo ? row.region?.id === f.geo : true) &&
        (f.culture ? row.culture.id === f.culture : true);

      return this.programs.filter(filter).map(mapToRow);
    },
  },
  methods: {
    reset() {
      this.filters = { ...defaultFilters };
    },
    editProgram(id) {
      this.$router.push({
        name: routeNames.userDefenceProgramEdit,
        params: { id },
      });
    },
    printProgram(id) {
      this.selectedPrintId = id;
    },
  },
};
</script>
